import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';

import {
    ButtonsBar,
    Panel,
    PanelHeader,
    StyledTextField,
    PlayTags,
    SimpleModal,
    PlayTagDisplay,
    QuillWrapper,
} from '@labradorsports/components';
import { noop, useSite } from '@labradorsports/utils';

import { CommunityProfileSetup } from '../../../community/index.js';
import { BasicTextEditor, addValidation, useValidation } from '../../../shared/index.js';
import {
    Selectors,
    useGetCurrentCommunityProfileQuery,
    useMyPlaybookQuery,
    useShareToCommunityMutation,
} from '../../../store/index.js';

interface Props {
    onCancel: () => void;
}

const SharePlayCommunity: FC<Props> = ({ onCancel }) => {
    const { PlayConfig } = useSite();
    const [shareToCommunity] = useShareToCommunityMutation();
    const {
        data: { plays = [] },
    } = useMyPlaybookQuery();
    const playbookId = useSelector(Selectors.playbookId);
    const shareConfig = useSelector(Selectors.shareConfig);
    const { data: communityProfile } = useGetCurrentCommunityProfileQuery();
    const { PlayTypes } = PlayConfig;

    const play = shareConfig ? plays.find((pl) => pl.id === shareConfig.itemIds[0]) : null;

    const [updatedName, setUpdatedName] = useState(play?.name ?? '');
    const [updatedTags, setUpdatedTags] = useState(play?.tags ?? []);
    const [updatedDescription, setUpdatedDescription] = useState(play?.description ?? '');
    const [showConfirmation, setShowConfirmation] = useState(false);

    const { validateRef, submitted, setSubmitted, errors } = useValidation(
        () => ({
            name: !updatedName,
            description: updatedDescription.length < 50,
            tags: updatedTags.length === 0,
        }),
        [updatedName, updatedTags, updatedDescription]
    );

    const cancel = () => {
        if (onCancel) {
            onCancel();
        }
    };

    const nameChange = (evt) => {
        setUpdatedName(evt.target.value);
    };

    const confirmShare = async () => {
        await shareToCommunity({
            playId: shareConfig.itemIds[0],
            name: updatedName,
            description: updatedDescription,
            tags: updatedTags,
            srcPlaybookId: playbookId,
        });
        onCancel();
    };

    const share = async () => {
        if (await validateRef.current()) {
            setShowConfirmation(true);
        } else {
            setSubmitted(true);
        }
    };

    const denyConfirmation = () => {
        setShowConfirmation(false);
    };

    const confirmationButtons = [
        {
            text: 'Cancel',
            action: denyConfirmation,
        },
        {
            text: 'Share',
            color: 'primary',
            action: confirmShare,
        },
    ];

    if (!communityProfile) {
        return <CommunityProfileSetup onSuccess={noop} onCancel={onCancel} />;
    }

    return play ? (
        <>
            {
                <SimpleModal open={showConfirmation} onClose={denyConfirmation} buttons={confirmationButtons}>
                    <h4>Review your entries</h4>
                    <p>
                        Posting as <em>{communityProfile?.screenName}</em>
                    </p>
                    <div className="text-start d-table mb-2">
                        <div className="d-table-row">
                            <div className="d-table-cell px-2">
                                <em>Name:</em>
                            </div>
                            <div className="d-table-cell">{updatedName}</div>
                        </div>
                        <div className="d-table-row">
                            <div className="d-table-cell px-2">
                                <em>Description:</em>
                            </div>
                            <div className="d-table-cell">
                                <QuillWrapper value={updatedDescription} readOnly />
                            </div>
                        </div>
                        <div className="d-table-row">
                            <div className="d-table-cell px-2">
                                <em>Tags:</em>
                            </div>
                            <div className="d-table-cell">
                                <PlayTagDisplay tags={updatedTags} />
                            </div>
                        </div>
                    </div>
                </SimpleModal>
            }
            <Panel
                className="share-play-community"
                noPadding
                header={<PanelHeader title={play.name} />}
                content={
                    <div className="p-3">
                        <p>Sharing to the Community Playbook</p>
                        <StyledTextField
                            label="Play Name"
                            value={updatedName}
                            onChange={nameChange}
                            helperText={errors.name && submitted && 'Please enter a name'}
                            error={errors.name && submitted}
                            fullWidth
                        />
                        <div>Play Description</div>
                        <BasicTextEditor value={updatedDescription} onChange={setUpdatedDescription} />
                        {errors.description && submitted ? (
                            <div className="small text-error">Please enter a description</div>
                        ) : null}
                        <div className="small text-muted">Must be at least 50 characters</div>
                        <p>Play Tags</p>
                        <PlayTags
                            value={updatedTags}
                            onChange={setUpdatedTags}
                            showDrillTags={play.type === PlayTypes.DRILL}
                        />
                        {errors.tags && submitted ? (
                            <div className="error-text small">Please select at least one tag</div>
                        ) : null}
                    </div>
                }
                footer={
                    <ButtonsBar>
                        <Button className="float-start" onClick={cancel}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="primary" onClick={share}>
                            Share
                        </Button>
                    </ButtonsBar>
                }
            />
        </>
    ) : null;
};

export default addValidation(SharePlayCommunity);
